<template>
  <!--
    backend#66 / F-46: Ein Fahrer legt sein Passwort selbst fest.

    Zwei Wege, eine Seite:
      /einladung/<token>  Link aus der Einladungsmail (nur auf Wunsch des
                          Disponenten verschickt)
      /einladung          E-Mail-Adresse + sechsstelliger Einmalcode, den der
                          Disponent vorgelesen hat
    Beides gilt 7 Tage und nur einmal. Danach meldet sich der Fahrer in der
    Fahrer-App an - hier nicht (die Anmeldung im Browser ist Fahrern verwehrt).
  -->
  <v-app>
    <alertComponent />
    <v-container class="background fill-height flex" fluid>
      <div class="mx-auto align-center justify-center">
        <v-card elevation="4" max-width="435" min-width="300" class="pt-2">
          <v-card-title>
            <p class="headline mb-0">Passwort festlegen</p>
          </v-card-title>

          <v-card-text v-if="fertig" data-cy="einladungFertig">
            <p>
              Dein Passwort ist gespeichert. Melde dich jetzt in der
              onDispatch-Fahrer-App an<span v-if="anmeldeAdresse">
                – mit <strong>{{ anmeldeAdresse }}</strong> und deinem neuen Passwort</span>.
            </p>
          </v-card-text>

          <v-form v-else ref="form" lazy-validation @submit.prevent="absenden">
            <v-card-text>
              <p v-if="mitLink && einladung">
                Hallo {{ einladung.name }}, lege hier dein persönliches Passwort für
                <strong>{{ einladung.email }}</strong> fest.
              </p>
              <p v-else-if="mitLink && !pruefeLink" class="warning--text" data-cy="einladungLinkUngueltig">
                Dieser Link ist ungültig, abgelaufen oder wurde bereits verwendet.
                Hast du einen Einmalcode von deinem Disponenten, gib ihn unten ein.
              </p>
              <p v-if="!mitLink || !einladung">
                Gib deine E-Mail-Adresse und den sechsstelligen Code ein, den du von
                deinem Disponenten erhalten hast.
              </p>

              <template v-if="!mitLink || !einladung">
                <v-text-field
                  prepend-icon="mdi-email"
                  v-model="form.email"
                  :rules="[(v) => !!v || 'Bitte gib deine E-Mail-Adresse ein.']"
                  label="E-Mail-Adresse"
                  type="email"
                  autocomplete="username"
                  data-cy="einladungEmail"
                ></v-text-field>
                <v-text-field
                  prepend-icon="mdi-numeric"
                  v-model="form.einmalcode"
                  :rules="[codeRegel]"
                  label="Einmalcode"
                  inputmode="numeric"
                  autocomplete="one-time-code"
                  maxlength="7"
                  data-cy="einladungCode"
                ></v-text-field>
              </template>

              <v-text-field
                prepend-icon="mdi-lock"
                v-model="form.password"
                :rules="passRules"
                :type="zeigen ? 'text' : 'password'"
                label="Neues Passwort"
                :hint="$t('registerForm.passwordHint')"
                autocomplete="new-password"
                data-cy="einladungPasswort"
              >
                <template v-slot:append>
                  <passwort-auge :sichtbar="zeigen" @umschalten="zeigen = !zeigen" />
                </template>
              </v-text-field>
              <v-text-field
                prepend-icon="mdi-lock"
                v-model="form.password2"
                :rules="[(v) => v === form.password || $t('registerForm.passwordsNotIdentical')]"
                :type="zeigen ? 'text' : 'password'"
                label="Passwort wiederholen"
                autocomplete="new-password"
                data-cy="einladungPasswort2"
              ></v-text-field>

              <p v-if="meldung" class="error--text" data-cy="einladungMeldung">{{ meldung }}</p>

              <v-btn
                block
                large
                color="primary"
                type="submit"
                :loading="laeuft"
                data-cy="einladungAbsenden"
                >Passwort speichern</v-btn
              >
            </v-card-text>
          </v-form>
        </v-card>
      </div>
    </v-container>
    <loginFooter />
  </v-app>
</template>

<script>
import PasswortAuge from "@/components/PasswortAuge.vue";
import { neuesPasswortRegeln } from "../util/passwort";
import loginFooter from "../components/loginFooter.vue";
import alertComponent from "../components/alertComponent.vue";

export const MELDUNGEN = {
  CODE_UNGUELTIG:
    "E-Mail-Adresse oder Code stimmen nicht, oder der Code ist abgelaufen. Bitte deinen Disponenten um einen neuen Code.",
  LINK_UNGUELTIG: "Dieser Link ist ungültig, abgelaufen oder wurde bereits verwendet.",
  PASSWORT_ZU_KURZ: "Das Passwort muss mindestens 12 Zeichen lang sein.",
  GEBREMST: "Zu viele Versuche. Bitte warte einige Minuten und versuche es dann erneut.",
  FEHLER: "Das Passwort konnte gerade nicht gespeichert werden. Bitte versuche es später erneut.",
};

export default {
  name: "Einladung",
  components: { PasswortAuge, loginFooter, alertComponent },
  props: { token: { type: String, default: "" } },
  data() {
    return {
      einladung: null,
      pruefeLink: !!this.token,
      form: { email: "", einmalcode: "", password: "", password2: "" },
      //dieselben Regeln wie Registrierung und Zuruecksetzen (login#4/#5)
      passRules: [(v) => !!v || this.$t("generalForms.pass.required"), ...neuesPasswortRegeln().slice(1)],
      zeigen: false,
      laeuft: false,
      meldung: "",
      fertig: false,
    };
  },
  computed: {
    mitLink() {
      return !!this.token;
    },
    anmeldeAdresse() {
      return (this.einladung && this.einladung.email) || this.form.email.trim().toLowerCase();
    },
  },
  mounted() {
    if (!this.token) return;
    return this.$store.dispatch("einladungStatus", this.token).then((einladung) => {
      this.einladung = einladung;
      this.pruefeLink = false;
    });
  },
  methods: {
    codeRegel(v) {
      return /^[0-9]{6}$/.test(String(v || "").replace(/[\s-]/g, "")) || "Der Code hat sechs Ziffern.";
    },
    absenden() {
      if (this.$refs.form && !this.$refs.form.validate()) return;
      this.meldung = "";
      this.laeuft = true;
      const daten =
        this.mitLink && this.einladung
          ? { token: this.token, password: this.form.password }
          : { email: this.form.email, einmalcode: this.form.einmalcode, password: this.form.password };
      return this.$store
        .dispatch("einladungEinloesen", daten)
        .then((ergebnis) => {
          if (ergebnis === "OK") {
            this.fertig = true;
          } else {
            this.meldung = MELDUNGEN[ergebnis] || MELDUNGEN.FEHLER;
          }
        })
        .finally(() => {
          this.laeuft = false;
        });
    },
  },
};
</script>

<style scoped>
.background {
  background-color: #f3f3fa;
}
</style>
